/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    Button,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
    useToast,
} from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import ListState from "../../../../components/ListState/ListState";
import Navbar from "../../../../components/Navbar/Navbar";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import {
    useExportTerminatedClients,
    useGetTerminatedClients,
} from "../../hooks/queries/reports.queries";
import { ITerminatedClient } from "../../types/reports.types";
import {
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import FilterButton from "../../../../shared-ui/Buttons/FilterButton/FilterButton";
import { useFetchInsuranceProviders } from "../../../clients/hooks/clients.queries";
import CheckPrimaryColorIcon from "../../../../components/Icons/CheckPrimaryColor";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";

export default function TerminatedClientsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isExporting, setIsExporting] = React.useState(false);

    const searchString = searchParams.get("search") || "";
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [providerSearchString, setProviderSearchString] = React.useState("");
    const clientFilter = searchParams.get("client_id") || "";
    const providerFilter = searchParams.get("provider_id") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const insuranceProviderFilter =
        searchParams.get("insurance_provider") || "";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const toast = useToast();

    const insuranceProviders = useFetchInsuranceProviders();

    const search = useDebounce(searchString, 500);
    const debouncedProviderSearchValue = useDebounce(providerSearchString, 500);
    const debouncedClientSearchValue = useDebounce(clientSearchString, 500);

    const {
        data: clients,
        isLoading: clientsLoading,
        error: clientsError,
    } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearchValue,
    });

    const {
        data: providers,
        isLoading: providerListLoading,
        error: providerListError,
    } = useFetchProviders({
        page: 1,
        search_string: debouncedProviderSearchValue,
        limit: 20,
    });

    const { data, error, isLoading, isSuccess } = useGetTerminatedClients({
        search_string: search,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        insurance_provider_id: insuranceProviderFilter,
        client_id: clientFilter,
        provider_id: providerFilter,
        page: pageFilter,
        limit: limitFilter,
    });

    const exportQuery = useExportTerminatedClients(
        {
            search_string: search,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            insurance_provider_id: insuranceProviderFilter,
            client_id: clientFilter,
            provider_id: providerFilter,
        },
        isExporting
    );

    const getInsuranceTextToDisplay = React.useMemo(
        () =>
            insuranceProviders?.data?.data.find(
                (provider) =>
                    provider.insurance_provider_id === insuranceProviderFilter
            )?.alias,
        [insuranceProviderFilter, insuranceProviders?.data?.data]
    );

    const getClientFilterTextToDisplay = React.useMemo(() => {
        const selectedClient = clients?.data.find(
            (client) => client.client_id === clientFilter
        );
        if (!selectedClient) return null;

        return displayNameInRightFormat({
            firstName: selectedClient.first_name,
            lastName: selectedClient.last_name,
        });
    }, [clientFilter, clients?.data]);

    const getProviderFilterTextToDisplay = React.useMemo(() => {
        const selectedProvider = providers?.data.find(
            (provider) => provider.provider_id === providerFilter
        );
        if (!selectedProvider) return null;

        return displayNameInRightFormat(selectedProvider.provider_name);
    }, [providerFilter, providers?.data]);

    const handleExportClick = async () => {
        setIsExporting(true);
        await exportQuery.refetch();
        setIsExporting(false);
        if (exportQuery.error) {
            toast.toast({
                mode: "error",
                message: "Could not export terminated clients report.",
            });
        }
    };

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleInsuranceProviderChange = (value: string) => {
        if (value === "") searchParams.delete("insurance_provider");
        else searchParams.set("insurance_provider", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleClientFilter = (client_id: string) => {
        if (client_id === "") {
            searchParams.delete("client_id");
        } else {
            searchParams.set("client_id", client_id);
        }
        setSearchParams(searchParams);
    };

    const handleProviderFilter = (provider_id: string) => {
        if (provider_id === "") {
            searchParams.delete("provider_id");
        } else {
            searchParams.set("provider_id", provider_id);
        }
        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for limit
    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    let terminatedClientsCount;
    if (error) terminatedClientsCount = "";
    else if (isLoading) terminatedClientsCount = "";
    else if (data?.data && data?.data.length === 0) terminatedClientsCount = 0;
    else if (data?.data && data?.data.length > 0)
        terminatedClientsCount = data.total_count;

    const columns: ColumnDef<ITerminatedClient>[] = [
        {
            accessorKey: "client_first_name",
            header: "CLIENT'S NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.client_first_name,
                    lastName: row.original.client_last_name,
                }),
        },
        {
            accessorKey: "session_date",
            header: "TERMINATION DATE",
            cell: ({ row }) => (
                <span className="flex flex-col items-start gap-y-8">
                    <span>{formatDate(row.original.session_date)}</span>
                    <span>
                        (
                        {formatDate(
                            row.original.start_date_time,
                            false,
                            "hh:mmaaa"
                        )}{" "}
                        -{" "}
                        {formatDate(
                            row.original.end_date_time,
                            false,
                            "hh:mmaaa"
                        )}
                        )
                    </span>
                </span>
            ),
        },
        {
            accessorKey: "provider_first_name",
            header: "PROVIDER'S NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.provider_first_name,
                    lastName: row.original.provider_last_name,
                }),
        },
        {
            accessorKey: "service",
            header: "SERVICE",
            cell: ({ row }) => (
                <span className="capitalize">
                    {removeEnumUnderscore(row.original.service)}
                </span>
            ),
        },
        {
            accessorKey: "insurance_provider_name",
            header: "INSURANCE PROVIDER",
            cell: ({ row }) => (
                <span className="uppercase">
                    {row.original.insurance_provider_name
                        ? row.original.insurance_provider_name
                        : "--"}
                </span>
            ),
        },
    ];

    return (
        <>
            <Navbar title={`Terminated Clients(${terminatedClientsCount})`} />

            <div className="page">
                <div className=" bg-white rounded-r8 px-12 py-16 flex flex-col gap-y-24 mb-24">
                    <SearchInput
                        placeholder="Search by client name, provider name"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />

                    <div className="flex justify-between items-center flex-wrap">
                        <div className="flex gap-x-12 gap-y-24 flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        classNames={
                                            getClientFilterTextToDisplay
                                                ? "uppercase"
                                                : "normal-case"
                                        }
                                        text={
                                            (clientsLoading && "Loading..") ||
                                            (clientsError &&
                                                "Error loading clients") ||
                                            (clients?.data &&
                                            getClientFilterTextToDisplay
                                                ? truncateString(
                                                      getClientFilterTextToDisplay
                                                  )
                                                : "Client")
                                        }
                                    />
                                </DropdownTrigger>
                                <DropdownContent
                                    width="300px"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                >
                                    <SearchInput
                                        placeholder="Search by client name"
                                        containerClass="h-24"
                                        onKeyDown={(e) => {
                                            if (e.key !== "Escape") {
                                                e.stopPropagation();
                                            }
                                        }}
                                        onChange={(e) => {
                                            setClientSearchString(
                                                e.target.value
                                            );
                                        }}
                                    />
                                    {clientsLoading && (
                                        <div className="flex flex-col gap-y-4 items-stretch w-full">
                                            {[1, 2, 3, 4, 5].map((item) => (
                                                <Skeleton
                                                    key={item}
                                                    width="100%"
                                                    height="24px"
                                                    containerTestId="clients-loader"
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {clientsError && (
                                        <ListState
                                            isError
                                            context="general"
                                            stateHelperText="Please try again later"
                                            errorMsg={
                                                providerListError?.response
                                                    ?.data?.message ||
                                                "Error loading clients"
                                            }
                                        />
                                    )}
                                    {clients && (
                                        <>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleClientFilter("")
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            !clientFilter,
                                                    }
                                                )}
                                                closeMenuOnSelect
                                            >
                                                ALL
                                            </DropdownItem>
                                            {clients?.data?.map((client) => (
                                                <DropdownItem
                                                    key={client.client_id}
                                                    onClick={() =>
                                                        handleClientFilter(
                                                            client.client_id
                                                        )
                                                    }
                                                    className={cn(
                                                        "text-xs font-medium",
                                                        {
                                                            "text-primary":
                                                                clientFilter ===
                                                                client.client_id,
                                                        }
                                                    )}
                                                    itemValue={client.client_id}
                                                >
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </DropdownItem>
                                            ))}
                                        </>
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        classNames={
                                            getProviderFilterTextToDisplay
                                                ? "uppercase"
                                                : "normal-case"
                                        }
                                        text={
                                            (providerListLoading &&
                                                "Loading..") ||
                                            (providerListError &&
                                                "Error loading providers") ||
                                            (providers?.data &&
                                            getProviderFilterTextToDisplay
                                                ? truncateString(
                                                      getProviderFilterTextToDisplay
                                                  )
                                                : "Provider")
                                        }
                                    />
                                </DropdownTrigger>
                                <DropdownContent
                                    align="end"
                                    width="300px"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                    collisionPadding={10}
                                >
                                    <SearchInput
                                        placeholder="Search by provider name"
                                        containerClass="h-24"
                                        onKeyDown={(e) => {
                                            if (e.key !== "Escape") {
                                                e.stopPropagation();
                                            }
                                        }}
                                        onChange={(e) =>
                                            setProviderSearchString(
                                                e.target.value
                                            )
                                        }
                                    />
                                    {providerListLoading && (
                                        <div className="flex flex-col gap-y-4 items-stretch w-full">
                                            {[1, 2, 3, 4, 5].map((item) => (
                                                <Skeleton
                                                    key={item}
                                                    width="100%"
                                                    height="24px"
                                                    containerTestId="provider-loader"
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {providerListError && (
                                        <ListState
                                            isError
                                            context="general"
                                            stateHelperText="Please try again later"
                                            errorMsg={
                                                providerListError?.response
                                                    ?.data?.message ||
                                                "Error loading providers"
                                            }
                                        />
                                    )}
                                    {providers && (
                                        <>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleProviderFilter("")
                                                }
                                                className={cn(
                                                    "text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            !providerFilter,
                                                    }
                                                )}
                                                closeMenuOnSelect
                                            >
                                                ALL
                                            </DropdownItem>

                                            {providers?.data?.map(
                                                (provider) => (
                                                    <DropdownItem
                                                        key={
                                                            provider.provider_id
                                                        }
                                                        onClick={() =>
                                                            handleProviderFilter(
                                                                provider.provider_id
                                                            )
                                                        }
                                                        itemValue={
                                                            provider.provider_id
                                                        }
                                                        className={cn(
                                                            "text-xs font-medium",
                                                            {
                                                                "text-primary":
                                                                    providerFilter ===
                                                                    provider.provider_id,
                                                            }
                                                        )}
                                                        closeMenuOnSelect
                                                    >
                                                        {displayNameInRightFormat(
                                                            provider.provider_name
                                                        )}
                                                    </DropdownItem>
                                                )
                                            )}
                                        </>
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        classNames={
                                            getInsuranceTextToDisplay
                                                ? "uppercase"
                                                : "normal-case"
                                        }
                                        text={
                                            (insuranceProviders.isLoading &&
                                                "Loading..") ||
                                            (insuranceProviders.error &&
                                                "Error loading providers") ||
                                            (insuranceProviders?.data?.data &&
                                            getInsuranceTextToDisplay
                                                ? truncateString(
                                                      removeEnumUnderscore(
                                                          getInsuranceTextToDisplay ||
                                                              ""
                                                      )
                                                  )
                                                : "Insurance")
                                        }
                                    />
                                </DropdownTrigger>
                                <DropdownContent
                                    align="end"
                                    width="auto"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                >
                                    <DropdownItem
                                        className={cn(
                                            "flex gap-x-8 items-center text-xs font-medium",
                                            {
                                                "text-primary":
                                                    insuranceProviderFilter ===
                                                    "",
                                            }
                                        )}
                                        onClick={() =>
                                            handleInsuranceProviderChange("")
                                        }
                                    >
                                        All
                                        {insuranceProviderFilter === "" ? (
                                            <CheckPrimaryColorIcon />
                                        ) : null}
                                    </DropdownItem>
                                    {insuranceProviders?.data?.data
                                        .sort((a, b) =>
                                            a.alias.localeCompare(b.alias)
                                        )
                                        .map((provider) => (
                                            <DropdownItem
                                                className={cn(
                                                    "flex gap-x-8 items-center uppercase text-xs font-medium",
                                                    {
                                                        "text-primary":
                                                            insuranceProviderFilter ===
                                                            provider.insurance_provider_id,
                                                    }
                                                )}
                                                key={
                                                    provider.insurance_provider_id
                                                }
                                                onClick={() =>
                                                    handleInsuranceProviderChange(
                                                        provider.insurance_provider_id
                                                    )
                                                }
                                            >
                                                {provider.alias}
                                                {insuranceProviderFilter ===
                                                provider.insurance_provider_id ? (
                                                    <CheckPrimaryColorIcon />
                                                ) : null}
                                            </DropdownItem>
                                        ))}
                                </DropdownContent>
                            </Dropdown>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton text="Date" />
                                </PopoverTrigger>
                                <PopoverContent
                                    align="end"
                                    className="p-16 gap-y-12"
                                >
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={fromDateFilterToDate}
                                        onChange={handleFromDateFilterChange}
                                        className="hover:border-primary"
                                        maxDate={
                                            toDateFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                    />
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        className="hover:border-primary"
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <Button
                            onClick={handleExportClick}
                            disabled={isExporting || data?.data.length === 0}
                        >
                            {isExporting ? "Exporting..." : "Export"}
                        </Button>
                    </div>
                </div>

                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display terminated clients at this time please try again later`
                        }
                    />
                )}

                {isLoading && (
                    <Skeleton
                        type="table"
                        containerTestId="terminated-clients-loader"
                        count={6}
                    />
                )}

                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Terminated clients will appear here once they are added"
                        isError={false}
                        emptyMessage="No terminated clients added yet"
                    />
                )}

                {isSuccess && data && data.data.length > 0 && (
                    <Table
                        data={data.data}
                        columns={columns}
                        hasPagination={data.total_count > 20}
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handlePageChange}
                                limit={limitFilter}
                                onLimitChange={handleLimitChange}
                            />
                        }
                    />
                )}
            </div>
        </>
    );
}
